import { useState, useEffect, useRef } from 'react';
import { Clock, User, File as FileEdit, Plus, Trash2, RefreshCw } from 'lucide-react';
import { activityLogService, ActivityLog } from '../services/activityLogService';
import { useLanguage } from '../contexts/LanguageContext';
import { log } from '../lib/logger';

interface CampaignActivityLogProps {
  campaignId: string;
}

interface ResolvedNames {
  [uuid: string]: string;
}

export function CampaignActivityLog({ campaignId }: CampaignActivityLogProps) {
  const { t } = useLanguage();
  const [logs, setLogs] = useState<ActivityLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [resolvedNames, setResolvedNames] = useState<ResolvedNames>({});
  const resolvedNamesRef = useRef<ResolvedNames>({});

  useEffect(() => {
    resolvedNamesRef.current = resolvedNames;
  }, [resolvedNames]);

  useEffect(() => {
    loadLogs();
  }, [campaignId]);

  useEffect(() => {
    if (logs.length === 0) return;

    const resolveAgentNames = async () => {
      const agentIds = new Set<string>();
      logs.forEach(log => {
        if (activityLogService.isAgentIdField(log.field_name)) {
          if (log.old_value && log.old_value !== '-') agentIds.add(log.old_value);
          if (log.new_value && log.new_value !== '-') agentIds.add(log.new_value);
        }
      });

      const newNames: ResolvedNames = {};
      for (const id of agentIds) {
        if (!resolvedNamesRef.current[id]) {
          const name = await activityLogService.resolveAgentName(id);
          if (name) newNames[id] = name;
        }
      }

      if (Object.keys(newNames).length > 0) {
        setResolvedNames(prev => ({ ...prev, ...newNames }));
      }
    };

    resolveAgentNames();
  }, [logs]);

  const loadLogs = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await activityLogService.getActivityLogs(campaignId);
      setLogs(data);
    } catch (err) {
      log.error('Error loading activity logs:', err);
      setError(t('activity.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const getActionIcon = (action: string) => {
    switch (action) {
      case 'created':
        return <Plus className="w-4 h-4" />;
      case 'deleted':
        return <Trash2 className="w-4 h-4" />;
      case 'field_changed':
      case 'updated':
        return <FileEdit className="w-4 h-4" />;
      default:
        return <FileEdit className="w-4 h-4" />;
    }
  };

  const getActionColor = (action: string) => {
    switch (action) {
      case 'created':
        return 'bg-emerald-100 text-emerald-700 border-emerald-200';
      case 'deleted':
        return 'bg-red-100 text-red-700 border-red-200';
      case 'field_changed':
      case 'updated':
        return 'bg-blue-100 text-blue-700 border-blue-200';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const getTimelineColor = (action: string) => {
    switch (action) {
      case 'created':
        return 'bg-emerald-500';
      case 'deleted':
        return 'bg-red-500';
      case 'field_changed':
      case 'updated':
        return 'bg-blue-500';
      default:
        return 'bg-gray-400';
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return t('activity.justNow');
    if (diffMins < 60) return `${diffMins}m ${t('activity.ago')}`;
    if (diffHours < 24) return `${diffHours}h ${t('activity.ago')}`;
    if (diffDays < 7) return `${diffDays}d ${t('activity.ago')}`;

    return date.toLocaleDateString(undefined, {
      day: 'numeric',
      month: 'short',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
    });
  };

  const formatFullDate = (dateString: string) => {
    return new Date(dateString).toLocaleString(undefined, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getActionText = (log: ActivityLog) => {
    const fieldName = activityLogService.getFieldDisplayName(log.field_name || '');

    switch (log.action) {
      case 'created':
        return t('activity.created');
      case 'deleted':
        return t('activity.deleted');
      case 'field_changed':
        return `${t('activity.changed')} ${fieldName}`;
      case 'updated':
        return t('activity.updated');
      default:
        return log.action;
    }
  };

  const getUserDisplayName = (email: string) => {
    const parts = email.split('@');
    return parts[0] || email;
  };

  const formatDisplayValue = (value: string | null, fieldName: string | null): string => {
    if (!value || value === '-') return '-';

    if (activityLogService.isAgentIdField(fieldName) && resolvedNames[value]) {
      return resolvedNames[value];
    }

    return activityLogService.formatActivityValue(value, fieldName || undefined);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="flex items-center gap-3 text-gray-500">
          <RefreshCw className="w-5 h-5 animate-spin" />
          <span>{t('common.loading')}</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <div className="text-red-500 mb-4">{error}</div>
        <button
          onClick={loadLogs}
          className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium transition-colors"
        >
          {t('common.retry')}
        </button>
      </div>
    );
  }

  if (logs.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <Clock className="w-12 h-12 text-gray-300 mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">{t('activity.noActivity')}</h3>
        <p className="text-gray-500 text-sm max-w-sm">
          {t('activity.noActivityDescription')}
        </p>
      </div>
    );
  }

  const groupedByDate = logs.reduce((acc, log) => {
    const date = new Date(log.created_at).toLocaleDateString();
    if (!acc[date]) acc[date] = [];
    acc[date].push(log);
    return acc;
  }, {} as Record<string, ActivityLog[]>);

  return (
    <div className="relative">
      {Object.entries(groupedByDate).map(([date, dateLogs], groupIndex) => (
        <div key={date} className="mb-8 last:mb-0">
          <div className="sticky top-0 bg-white z-10 pb-3">
            <span className="inline-flex items-center px-3 py-1 bg-gray-100 rounded-full text-xs font-medium text-gray-600">
              {new Date(dateLogs[0].created_at).toLocaleDateString(undefined, {
                weekday: 'long',
                day: 'numeric',
                month: 'long',
              })}
            </span>
          </div>

          <div className="relative pl-8">
            <div className="absolute left-3 top-0 bottom-0 w-px bg-gray-200" />

            {dateLogs.map((log, index) => (
              <div key={log.id} className="relative mb-6 last:mb-0">
                <div
                  className={`absolute left-[-20px] w-2.5 h-2.5 rounded-full ${getTimelineColor(log.action)} ring-4 ring-white`}
                />

                <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between gap-4 mb-3">
                    <div className="flex items-center gap-2">
                      <div className={`p-1.5 rounded-lg border ${getActionColor(log.action)}`}>
                        {getActionIcon(log.action)}
                      </div>
                      <span className="font-medium text-gray-900">
                        {getActionText(log)}
                      </span>
                    </div>

                    <div className="flex items-center gap-1.5 text-xs text-gray-500" title={formatFullDate(log.created_at)}>
                      <Clock className="w-3.5 h-3.5" />
                      <span>{formatDate(log.created_at)}</span>
                    </div>
                  </div>

                  {log.action === 'field_changed' && (log.old_value || log.new_value) && (
                    <div className="flex items-center gap-3 mb-3 text-sm">
                      <div className="flex-1 min-w-0">
                        <span className="text-xs text-gray-500 uppercase tracking-wider">{t('activity.from')}</span>
                        <div className="mt-1 px-3 py-2 bg-red-50 border border-red-100 rounded-lg text-red-700 truncate" title={log.old_value || '-'}>
                          {formatDisplayValue(log.old_value, log.field_name)}
                        </div>
                      </div>

                      <div className="text-gray-400 pt-5">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                        </svg>
                      </div>

                      <div className="flex-1 min-w-0">
                        <span className="text-xs text-gray-500 uppercase tracking-wider">{t('activity.to')}</span>
                        <div className="mt-1 px-3 py-2 bg-emerald-50 border border-emerald-100 rounded-lg text-emerald-700 truncate" title={log.new_value || '-'}>
                          {formatDisplayValue(log.new_value, log.field_name)}
                        </div>
                      </div>
                    </div>
                  )}

                  {log.action === 'created' && log.new_value && (
                    <div className="text-sm text-gray-600 mb-3">
                      <span className="text-gray-500">{t('activity.campaignName')}:</span>{' '}
                      <span className="font-medium">{log.new_value}</span>
                    </div>
                  )}

                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <User className="w-3.5 h-3.5" />
                    <span>{getUserDisplayName(log.user_email)}</span>
                    <span className="text-gray-300">|</span>
                    <span className="text-gray-400">{log.user_email}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
